import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';

import { useStyles } from '../checkoutDrawer.styles';
import LoadingAnimation from '../../LoadingAnimation';
import AddOnsSummary from './AddOnsSummary';

function OrderSummary ({
    plan,
    planNameUpper,
    basePrice,
    showBasePrice,
    effectivePrice,
    discountAmount,
    promoApplied,
    addOns,
    subTotal,
    taxLabel,
    taxRate,
    taxValue,
    totalRecurring,
    previewLoading,
    formatPriceUnit,
}) {
    const classes = useStyles();

    // Non-breaking space keeps the cell's text line box (and baseline) intact
    // while the animation is overlaid absolutely, so the row height and the
    // label baseline stay identical between loading and loaded states.
    const loadingOverlay = (
        <>
            {' '}
            <Box className={classes.summaryLoadingBox}><LoadingAnimation /></Box>
        </>
    );

    return (
        <div className={classes.summaryCard} aria-label="Order summary">
            <div className={classes.summaryTopRow}>
                <div className={classes.summaryPlanInfo}>
                    <span className={classes.summaryPlanLabel}>{planNameUpper}</span>
                    {plan.description && (
                        <span className={classes.summaryPlanDesc}>{plan.description}</span>
                    )}
                </div>
                {showBasePrice && (
                    <span className={classes.summaryPrice}>
                        <strong>{formatPriceUnit(basePrice)}</strong>
                        <span className={classes.summaryPricePeriod}>/mo</span>
                    </span>
                )}
            </div>

            <AddOnsSummary addOns={addOns} formatPriceUnit={formatPriceUnit} />

            <div className={classes.summaryDivider} />

            <div className={classes.summaryRow}>
                <span className={classes.summaryLabel}>Sub-Total</span>
                <span className={classes.summaryValue}>{formatPriceUnit(subTotal)}</span>
            </div>
            <div className={classes.summaryRow}>
                <span className={classes.summaryLabel}>{taxLabel} ({taxRate}%)</span>
                <span className={classes.summaryValue}>
                    {previewLoading ? loadingOverlay : formatPriceUnit(taxValue)}
                </span>
            </div>
            {promoApplied && (
                <div className={classes.summaryRow}>
                    <span className={classes.summaryDiscount}>Promo discount</span>
                    <span className={classes.summaryDiscountAmount}>–{formatPriceUnit(discountAmount)}</span>
                </div>
            )}

            <div className={classes.summaryDivider} />

            <div className={classes.summaryRow}>
                <span className={classes.summaryDueLabel}>Due today</span>
                <span className={classes.summaryDueAmount}>
                    {previewLoading ? loadingOverlay : formatPriceUnit(effectivePrice)}
                </span>
            </div>
            <div className={classes.summaryRow}>
                <span className={classes.summaryLabel}>Total Recurring</span>
                <span className={classes.summaryValue}>
                    {previewLoading ? loadingOverlay : formatPriceUnit(totalRecurring)}
                </span>
            </div>
        </div>
    );
}

OrderSummary.propTypes = {
    plan: PropTypes.shape({ description: PropTypes.string }).isRequired,
    planNameUpper: PropTypes.string.isRequired,
    basePrice: PropTypes.number.isRequired,
    showBasePrice: PropTypes.bool,
    effectivePrice: PropTypes.number.isRequired,
    discountAmount: PropTypes.number.isRequired,
    promoApplied: PropTypes.bool,
    addOns: PropTypes.array,
    subTotal: PropTypes.number.isRequired,
    taxLabel: PropTypes.string.isRequired,
    taxRate: PropTypes.number.isRequired,
    taxValue: PropTypes.number.isRequired,
    totalRecurring: PropTypes.number.isRequired,
    previewLoading: PropTypes.bool,
    formatPriceUnit: PropTypes.func.isRequired,
};

OrderSummary.defaultProps = {
    showBasePrice: true,
    promoApplied: false,
    addOns: [],
    previewLoading: false,
};

export default OrderSummary;
